import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useMapStore } from '../stores/useMapStore';
import type { Map as MapLibreMap } from 'maplibre-gl';

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

interface BboxOverlayProps {
  mapRef: React.RefObject<MapLibreMap | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

const HANDLE_SIZE = typeof window !== 'undefined' && 'ontouchstart' in window ? 44 : 12;
const MIN_SIZE = 120;
const MAX_SIZE = 600;

export const BboxOverlay: React.FC<BboxOverlayProps> = ({ mapRef, containerRef }) => {
  const selectionMode = useMapStore((s) => s.selectionMode);
  const setBbox = useMapStore((s) => s.setBbox);
  const setCenter = useMapStore((s) => s.setCenter);

  const debouncedSetBbox = useCallback(
    debounce((bbox: [number, number, number, number] | null) => {
      useMapStore.getState().setBbox(bbox);
    }, 100),
    []
  );

  const debouncedSetCenter = useCallback(
    debounce((c: [number, number]) => {
      useMapStore.getState().setCenter(c);
    }, 100),
    []
  );

  const [width, setWidth] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 640 ? 200 : 300;
  });
  const [height, setHeight] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 640 ? 200 : 300;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [activeHandle, setActiveHandle] = useState<ResizeHandle | null>(null);

  const resizeStart = useRef<{
    x: number;
    y: number;
    w: number;
    h: number;
  }>({ x: 0, y: 0, w: 0, h: 0 });

  // Update bbox from current map center + overlay dimensions
  const updateBboxCoordinates = useCallback(() => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container) return;

    const cWidth = container.clientWidth;
    const cHeight = container.clientHeight;
    const left = (cWidth - width) / 2;
    const right = (cWidth + width) / 2;
    const top = (cHeight - height) / 2;
    const bottom = (cHeight + height) / 2;

    const nw = map.unproject([left, top]);
    const se = map.unproject([right, bottom]);

    debouncedSetCenter([map.getCenter().lng, map.getCenter().lat]);
    debouncedSetBbox([nw.lng, se.lat, se.lng, nw.lat]);
  }, [width, height, mapRef, containerRef, setBbox, setCenter]);

  // Update bbox on mount and when dimensions change
  useEffect(() => {
    updateBboxCoordinates();
  }, [width, height, updateBboxCoordinates]);

  // Listen to map move events to update bbox (throttled)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let lastRun = 0;
    const handler = () => {
      const now = Date.now();
      if (now - lastRun < 50) return;
      lastRun = now;
      updateBboxCoordinates();
    };
    map.on('move', handler);
    return () => {
      map.off('move', handler);
    };
  }, [mapRef, updateBboxCoordinates]);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent, handle: ResizeHandle) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setActiveHandle(handle);
      resizeStart.current = {
        x: e.clientX,
        y: e.clientY,
        w: width,
        h: height,
      };

      // Capture pointer to track outside the handle
      (e.target as Element).setPointerCapture(e.pointerId);
    },
    [width, height]
  );

  useEffect(() => {
    if (!isResizing || !activeHandle) return;

    const handlePointerMove = (e: PointerEvent) => {
      const dx = e.clientX - resizeStart.current.x;
      const dy = e.clientY - resizeStart.current.y;

      let newW = width;
      let newH = height;

      switch (activeHandle) {
        case 'ne':
        case 'e':
          newW = resizeStart.current.w + dx * 2;
          break;
        case 'sw':
        case 'w':
          newW = resizeStart.current.w - dx * 2;
          break;
        case 'n':
          newH = resizeStart.current.h - dy * 2;
          break;
        case 'se':
        case 's':
          newH = resizeStart.current.h + dy * 2;
          break;
        case 'nw':
          newW = resizeStart.current.w - dx * 2;
          newH = resizeStart.current.h - dy * 2;
          break;
      }

      setWidth(Math.max(MIN_SIZE, Math.min(MAX_SIZE, newW)));
      setHeight(Math.max(MIN_SIZE, Math.min(MAX_SIZE, newH)));
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      setActiveHandle(null);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isResizing, activeHandle, width, height]);

  if (selectionMode !== 'bbox') return null;

  const handleStyle = (pos: string): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: HANDLE_SIZE,
      height: HANDLE_SIZE,
      backgroundColor: '#38bdf8',
      border: '2px solid #0f172a',
      borderRadius: '50%',
      zIndex: 20,
      touchAction: 'none',
      pointerEvents: 'auto',
    };

    switch (pos) {
      case 'nw':
        return { ...base, top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: 'nwse-resize' };
      case 'ne':
        return { ...base, top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: 'nesw-resize' };
      case 'sw':
        return { ...base, bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2, cursor: 'nesw-resize' };
      case 'se':
        return { ...base, bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2, cursor: 'nwse-resize' };
      case 'n':
        return { ...base, top: -HANDLE_SIZE / 2, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' };
      case 's':
        return { ...base, bottom: -HANDLE_SIZE / 2, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' };
      case 'w':
        return { ...base, left: -HANDLE_SIZE / 2, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' };
      case 'e':
        return { ...base, right: -HANDLE_SIZE / 2, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' };
      default:
        return base;
    }
  };

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
      <div
        style={{ width, height }}
        className="relative pointer-events-none"
      >
        {/* BBox fill */}
        <div className="absolute inset-0 rounded-lg border-2 border-sky-400/80 bg-sky-950/10 shadow-[0_0_20px_rgba(56,189,248,0.15)]">
          {/* Grid overlay */}
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 opacity-20 pointer-events-none">
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                className={`border-sky-400/30 ${i % 3 !== 2 ? 'border-r' : ''} ${i < 6 ? 'border-b' : ''}`}
              />
            ))}
          </div>

          {/* Size label */}
          <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-slate-950/80 text-[10px] text-sky-400 font-mono rounded border border-sky-500/20">
            SELECT AREA
          </div>
        </div>

        {/* Corner handles */}
        {(['nw', 'ne', 'sw', 'se'] as ResizeHandle[]).map((h) => (
          <div
            key={h}
            style={handleStyle(h)}
            onPointerDown={(e) => handleResizeStart(e, h)}
            className="hover:bg-sky-300 active:bg-sky-200 transition-colors"
          />
        ))}

        {/* Edge handles */}
        {(['n', 's', 'w', 'e'] as ResizeHandle[]).map((h) => (
          <div
            key={h}
            style={handleStyle(h)}
            onPointerDown={(e) => handleResizeStart(e, h)}
            className="hover:bg-sky-300 active:bg-sky-200 transition-colors"
          />
        ))}
      </div>
    </div>
  );
};
